import * as React from "react";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../misc/api/WebPortalTokenClient";
import { Column, Table } from "../Components/QueryTable";
import { ODataParams, Query, useQuery } from "../Components/Query";
import { MatchSortHeader } from "../Components/MatchSortHeader";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import DocumentTitle from "../misc/components/DocumentTitle";
import { DateMatchSortHeader } from "../Components/DateMatchSortHeader";
import { DateTimeAccuracy, LocalizedDateTime } from "../misc/components/LocalizedDateTime";
import { HamburgerMenu } from "../misc/components/HamburgerMenu";
import { useSecurityContext } from "../Core/SecurityContext";
import { Permission } from "../misc/api/apiTypes";
import { Alert } from "webportalclient";
import { SortHeader } from "../Components/SortHeader";
import { uniqBy } from "../misc/ArrayFunctions";
import AsyncActionButton from "../misc/components/AsyncActionButton";

const AlertNotificationTable = () => {
    const { data, applyFilters, filters, refresh } = useQuery();
    const { currentUserPermissions } = useSecurityContext();
    const { study } = useCurrentStudyConfigurationContext();
    const client = useWebPortalApiClient();
    const [error, setError] = React.useState<string | undefined>()
    const [availableTypes, setAvailableTypes] = React.useState([]);
    const [allNotifications, setAllNotifications] = React.useState([]);
    const [isMarkingAsRead, setIsMarkingAsRead] = React.useState(false);

    React.useEffect(() => {
        const getTypes = async () => {
            const result = await client.client.getAlertNotifications(undefined, `subscription/studyId eq ${study.id} or subscription/id eq null`, undefined, "subscription,alert");
            setAvailableTypes(uniqBy(result.value, (a, b) => a.subscription?.pluginName === b.subscription?.pluginName).map(a => ({
                value: a.subscription?.pluginName,
                label: a.subscription?.pluginName
            })));

            setAllNotifications(result.value);
        }
        getTypes();
    }, [study.id]);

    const clearAllFilters = () => {
        applyFilters([]);
    }

    const onMarkAsRead = async (alert: Alert, isRead: boolean) => {
        await client.client.patchAlertNotification({
            isRead: isRead,
        }, alert.id);
        refresh();
    }

    const onMarkAllAsRead = async (alerts: Alert[]) => {
        setIsMarkingAsRead(true);
        for (const alert of alerts.filter(a => !a.isRead)) {
            await client.client.patchAlertNotification({
                isRead: true,
            }, alert.id);
        }
        refresh();
        setIsMarkingAsRead(false);
    }

    const RenderHamburgerMenu = (alert: Alert) => {
        return (
            <HamburgerMenu>
                <li>
                    {alert.isRead ?
                        <a onClick={() => onMarkAsRead(alert, false)}>Mark as Unread</a>
                        : <a onClick={() => onMarkAsRead(alert, true)}>Mark as Read</a>
                    }
                </li>
            </HamburgerMenu>
        );
    };

    const readUnreadOptions = [{
        value: false,
        label: "Unread",
    }, {
        value: true,
        label: "Read",
    }]

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Notifications</h2>
                </div>
                <div className="col-md-6 h2">
                    <div className="pull-right">
                        <button className="btn btn-primary btn-outline" onClick={clearAllFilters}>
                            Clear All Filters
                        </button>&nbsp;
                        <AsyncActionButton isSubmitting={isMarkingAsRead} className="btn btn-primary btn-outline" onClick={() => onMarkAllAsRead(allNotifications)}>
                            Mark All as Read
                        </AsyncActionButton>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel panel-default" data-automationid="NotificationsPanelTable">
                        <div className="panel-body">
                            <Table data={data}>
                                <Column field="alert" wrapText={true} header={<SortHeader title="Headline" field="alert/headline" />} formatter={a => <><b>{a.headline}</b><p>{a.content}</p></>} />
                                <Column field="occurred" header={<DateMatchSortHeader title="Occurred" field="occurred" sortField="occurred" />} formatter={o => <LocalizedDateTime time={o} accuracy={DateTimeAccuracy.Second} />} />
                                <Column field="subscription" header={<MatchSortHeader title="Type" field="subscription/pluginname" options={availableTypes} sortField="subscription/pluginname" />} formatter={s => s?.pluginName} />
                                <Column field="alert" header={<SortHeader title="Session Name" field="alert/triggeredBySession/name" />} formatter={a => <><p>{a.triggeredBySession?.name ?? "-"}</p></>} />
                                <Column field="alert" header={<SortHeader title="Subject ID" field="alert/triggeredBySession/visit/subject/number" />} formatter={a => <><p>{a.triggeredBySession?.visit?.subject?.number ?? "-"}</p></>} />
                                <Column field="isRead" header={<MatchSortHeader title="Read" field="isRead" options={readUnreadOptions} sortField="isRead" />} formatter={iR => iR ? "Read" : <span className="text-danger">Unread</span>} />
                                <Column field="" formatter={n => RenderHamburgerMenu(n)} />
                            </Table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export const AlertNotificationList = () => {
    const client = useWebPortalApiClient();
    const { study } = useCurrentStudyConfigurationContext();

    const handleRefresh = async (params: ODataParams) => {
        let filter = params.filter ? `(${params.filter}) and ` : '';
        filter += `(subscription/studyId eq ${study.id} or subscription/id eq null)`;

        return await client.client.getAlertNotifications(undefined, filter, params.orderby, "subscription,alert($select=headline,content),alert($select=triggeredBySession;$expand=triggeredBySession($select=name,visit;$expand=visit($select=subject;$expand=subject($select=number;))))", params.top, params.skip, undefined, true);
    }

    return (
        <DocumentTitle title="Notifications | Vitalograph Web Portal">
            <Query onRefresh={handleRefresh} initialOrdering={[{ field: "isRead", condition: "asc" }]}>
                <AlertNotificationTable />
            </Query>
        </DocumentTitle>
    );
};
